import { ActionTree } from 'vuex';
import { Period, PeriodState } from '@/store/modules/periods/state';
import RootState from '@/store/root_state';
import Enrollments from '@/services/Enrollments';
import SentryClient from '@/helpers/sentry';
import {
  FilterClassroomsByPeriodType,
  WesterosClassroomType,
} from '@/models/enrollments/classroom.model';
import i18n from '@/i18n';

// We need to remove the classrooms Live de X (Geral) because when the user doesn't have first period classrooms
// appears as if he do. This happens because all the periods have the same Live de X classroom on Westeros-Enrollments.
const removeDefaultLiveClassroom = (
  filterClassroomsByPeriod: FilterClassroomsByPeriodType[],
): FilterClassroomsByPeriodType[] => {
  const classrooms: any = [];

  filterClassroomsByPeriod.forEach((filterClassroomByPeriod: any) => {
    const classroomsWithoutLive = filterClassroomByPeriod.classrooms.filter(
      (classroom: WesterosClassroomType) => !classroom.name.includes('(Geral)'),
    );

    if (classroomsWithoutLive.length > 0) {
      classrooms.push(filterClassroomByPeriod);
    }
  });

  return classrooms;
};

const actions: ActionTree<PeriodState, RootState> = {
  async PULL_USER_PERIODS({ commit }, payload): Promise<void> {
    const { course, classroomsIds } = payload;

    try {
      const filterClassroomsByPeriodResponse: FilterClassroomsByPeriodType[] | undefined =
        await Enrollments.filterClassroomsByPeriod(course, classroomsIds);

      if (!filterClassroomsByPeriodResponse || filterClassroomsByPeriodResponse.length === 0) {
        commit('SET_LOADING_PERIODS', false);

        return;
      }

      const classroomsByPeriod = removeDefaultLiveClassroom(filterClassroomsByPeriodResponse);

      const periods: number[] = classroomsByPeriod.map(
        (classroomByPeriod: FilterClassroomsByPeriodType) => classroomByPeriod.period,
      );

      const userHasComplementaryClassrooms: boolean = periods.some(
        (period: number) => period === 0,
      );

      // we only check if there's complementary classrooms because the period on electives is -1
      // so it goes to the last place on periods array, complementary go to the first
      const periodIndexOverflow: number = userHasComplementaryClassrooms ? 0 : 1;

      const periodsData: Period[] = periods.map((period: number, periodIndex: number) => {
        if (period === -1) {
          return { number: period, name: i18n.t('electives'), blocked: false };
        }

        if (period === 0) {
          return { number: period, name: i18n.t('complementaries'), blocked: false };
        }

        // this is not the best solution, but it is used to avoid the problem with the new intakes modules (
        // the new intake breaks a module into two submodules (with 3 and 2 disciplines each) )
        // the old users could have a problem with the module number (in the new intake it wont follow the old enumeration)  in the period modal display
        // we will fix it with the new discipline management
        return {
          number: period,
          name: i18n.t('periodNumber', { period: periodIndex + periodIndexOverflow }),
          blocked: false,
        };
      });

      // sorting the periodsData array so Eletivas and Complementares stay on top of the side menu
      periodsData.sort((a, b) => {
        if (a.number < b.number) {
          return -1;
        }

        return 1;
      });

      // if there is complementary and electives classrooms, we swap them to be in order alphabetically
      if (
        periodsData.find((period) => period.number === 0) &&
        periodsData.find((period) => period.number === -1)
      ) {
        [periodsData[0], periodsData[1]] = [periodsData[1], periodsData[0]];
      }

      const initialPeriod: Period = periodsData[periodsData.length - 1];

      commit('SET_USER_PERIODS', periodsData);
      commit('SET_USER_CURRENT_PERIOD', initialPeriod);
      commit('SET_ALL_CLASSROOMS_BY_PERIOD', classroomsByPeriod);
      commit('SET_CURRENT_CLASSROOMS_BY_PERIOD', initialPeriod.number);
    } catch (error) {
      SentryClient.captureError(error);
    }

    commit('SET_LOADING_PERIODS', false);
  },
};

export default actions;
